import AsyncStorage from '@react-native-async-storage/async-storage';
let _profUserId = 'default'; try { require('@react-native-async-storage/async-storage').default.getItem('userToken').then(function(t){if(t)_profUserId=t.slice(0,16);}).catch(function(){}); } catch(e) {}
/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  TextInput,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  ScrollView,
  Image,
  ActivityIndicator,
  StatusBar,
  Alert,
  Modal,
  KeyboardAvoidingView,
  Platform,
  RefreshControl,
  Animated,
  Dimensions,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome5,
} from '@expo/vector-icons';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import portfolioDatabase from '../../services/PortfolioDatabase';
import { apiFetch } from '../../utils/apiClient';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

// === API CONFIG ===
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const { width, height } = Dimensions.get('window');

export default function AllCertificationsScreen() {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  // === Live API Data ===
  const [userId, setUserId] = React.useState('default');
  React.useEffect(() => {
    AsyncStorage.getItem('userToken').then(t => { if (t) setUserId(t.slice(0, 16)); }).catch(() => {});
  }, []);
  React.useEffect(() => {
    if (userId === 'default') return;
    const _ck = `professional_${_profUserId}_allcertificationsscreen`;
    apiFetch('/api/data/customers?limit=1').then(d => {
      if (d && !d.error) {
        const arr = Array.isArray(d) ? d : (d.data || d.results || []);
        AsyncStorage.setItem(_ck, JSON.stringify(arr)).catch(() => {});
      }
    }).catch(() => {});
  }, [userId]);

  const navigation = useNavigation();

  // Data states
  const [certifications, setCertifications] = useState([]);
  const [filteredCertifications, setFilteredCertifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // UI states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedStatus, setSelectedStatus] = useState('All');
  const [sortBy, setSortBy] = useState('date');
  const [showFilters, setShowFilters] = useState(false);

  // Edit/Add states
  const [showEditModal, setShowEditModal] = useState(false);
  const [editMode, setEditMode] = useState('add');
  const [editingCertification, setEditingCertification] = useState(null);
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});

  // Animation
  const fadeAnim = useRef(new Animated.Value(0)).current;

  // Categories and statuses
  const categories = ['All', 'Professional', 'Technical', 'Management', 'Safety', 'Academic', 'Other'];
  const statuses = ['All', 'Active', 'Expired', 'Expiring Soon'];

  // Load data when screen comes into focus
  useFocusEffect(
    React.useCallback(() => {
      loadCertifications();
    }, [])
  );

  // Set up event listeners
  useEffect(() => {
    const setupEventListeners = () => {
      portfolioDatabase.subscribe('certification_added', (certification) => {
        setCertifications(prev => [certification, ...prev]);
      });

      portfolioDatabase.subscribe('certification_updated', (certification) => {
        setCertifications(prev => prev.map(c => c.id === certification.id ? certification : c));
      });

      portfolioDatabase.subscribe('certification_deleted', ({ id }) => {
        setCertifications(prev => prev.filter(c => c.id !== id));
      });
    };

    if (portfolioDatabase.initialized) {
      setupEventListeners();
    }

    return () => {
      // Cleanup if needed
    };
  }, []);

  // Apply filters and search
  useEffect(() => {
    applyFiltersAndSearch();
  }, [certifications, searchQuery, selectedCategory, selectedStatus, sortBy]);

  // Start animations
  useEffect(() => {
    if (!isLoading) {
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 600,
        useNativeDriver: true,
      }).start();
    }
  }, [isLoading]);

  const loadCertifications = async () => {
    try {
      setIsLoading(true);
      
      // Initialize database if not already done
      if (!portfolioDatabase.initialized) {
        await portfolioDatabase.initialize();
      }

      const certificationsData = await portfolioDatabase.getCertifications();
      setCertifications(certificationsData || []);
      
    } catch (error) {
      console.error('❌ Error loading certifications:', error);
      Alert.alert('Error', 'Failed to load certifications. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const applyFiltersAndSearch = () => {
    let filtered = [...certifications];

    // Apply search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(cert =>
        String(cert.title ?? '').toLowerCase().includes(query) ||
        String(cert.issuer ?? '').toLowerCase().includes(query) ||
        String(cert.description ?? '').toLowerCase().includes(query) ||
        (cert.credentialId && String(cert.credentialId ?? '').toLowerCase().includes(query))
      );
    }

    // Apply category filter
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(cert => 
        cert.category === selectedCategory
      );
    }

    // Apply status filter
    if (selectedStatus !== 'All') {
      filtered = filtered.filter(cert => {
        const status = getCertificationStatus(cert);
        return status === selectedStatus;
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'title':
          return a.title.localeCompare(b.title);
        case 'issuer':
          return a.issuer.localeCompare(b.issuer);
        case 'category':
          return a.category.localeCompare(b.category);
        case 'date':
        default:
          return new Date(b.date) - new Date(a.date);
      }
    });

    setFilteredCertifications(filtered);
  };

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await loadCertifications();
    } catch (error) {
      console.error('❌ Error refreshing:', error);
    } finally {
      setRefreshing(false);
    }
  };

  // Get certification status
  const getCertificationStatus = (cert) => {
    if (!cert.expiryDate || cert.expiryDate === 'No Expiry') {
      return 'Active';
    }

    const expiryDate = new Date(cert.expiryDate);
    const today = new Date();
    const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry < 0) {
      return 'Expired';
    } else if (daysUntilExpiry <= 90) {
      return 'Expiring Soon';
    } else {
      return 'Active';
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return '#10B981';
      case 'Expiring Soon': return '#F59E0B';
      case 'Expired': return '#EF4444';
      default: return '#64748B';
    }
  };

  // Form validation
  const validateForm = (data) => {
    const errors = {};

    if (!data.title?.trim()) errors.title = 'Certification title is required';
    if (!data.issuer?.trim()) errors.issuer = 'Issuer is required';
    if (!data.date?.trim()) errors.date = 'Issue date is required';
    if (!data.description?.trim()) errors.description = 'Description is required';

    return errors;
  };

  // Handle add certification
  const handleAddCertification = () => {
    setEditMode('add');
    setEditingCertification(null);
    setFormData({
      title: '',
      issuer: '',
      date: '',
      expiryDate: '',
      description: '',
      credentialId: '',
      verificationUrl: '',
      category: 'Professional',
      image: 'https://via.placeholder.com/300/0e4e96/FFFFFF?text=Certificate'
    });
    setFormErrors({});
    setShowEditModal(true);
  };

  // Handle edit certification
  const handleEditCertification = (certification) => {
    setEditMode('edit');
    setEditingCertification(certification);
    setFormData({ ...certification });
    setFormErrors({});
    setShowEditModal(true);
  };

  // Handle delete certification
  const handleDeleteCertification = (certification) => {
    Alert.alert(
      'Delete Certification',
      `Are you sure you want to delete "${certification.title}"? This action cannot be undone.`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              const success = await portfolioDatabase.deleteCertification(certification.id);
              if (success) {
                Alert.alert('Success', 'Certification deleted successfully');
              } else {
                Alert.alert('Error', 'Failed to delete certification');
              }
            } catch (error) {
              console.error('❌ Delete error:', error);
              Alert.alert('Error', 'Failed to delete certification');
            }
          }
        }
      ]
    );
  };

  // Handle save certification
  const handleSaveCertification = async () => {
    const errors = validateForm(formData);
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      Alert.alert('Validation Error', 'Please fix the errors in the form');
      return;
    }

    try {
      let result = null;

      if (editMode === 'add') {
        result = await portfolioDatabase.addCertification(formData);
      } else {
        result = await portfolioDatabase.updateCertification(editingCertification.id, formData);
      }

      if (result) {
        setShowEditModal(false);
        Alert.alert('Success', `Certification ${editMode === 'add' ? 'added' : 'updated'} successfully`);
      } else {
        Alert.alert('Error', `Failed to ${editMode} certification`);
      }
    } catch (error) {
      console.error('❌ Save error:', error);
      Alert.alert('Error', `Failed to ${editMode} certification`);
    }
  };

  // Render certification card
  const renderCertificationCard = ({ item: certification }) => {
    const status = getCertificationStatus(certification);
    const statusColor = getStatusColor(status);

    return (
      <TouchableOpacity
        style={styles.certificationCard}
        onPress={() => {
          Alert.alert(
            certification.title,
            `${certification.description}\n\nIssuer: ${certification.issuer}\nIssued: ${certification.date}${certification.expiryDate ? `\nExpires: ${certification.expiryDate}` : ''}`,
            [
              { text: 'Edit', onPress: () => handleEditCertification(certification) },
              { text: 'Delete', onPress: () => handleDeleteCertification(certification), style: 'destructive' },
              { text: 'Close', style: 'cancel' }
            ]
          );
        }}
        onLongPress={() => {
          Alert.alert(
            'Certification Options',
            `What would you like to do with "${certification.title}"?`,
            [
              { text: 'Edit', onPress: () => handleEditCertification(certification) },
              { text: 'Delete', onPress: () => handleDeleteCertification(certification), style: 'destructive' },
              { text: 'Cancel', style: 'cancel' }
            ]
          );
        }}
      >
        <View style={styles.certificationHeader}>
          <View style={styles.certificationIcon}>
            <MaterialCommunityIcons name="certificate" size={24} color="#0e4e96" />
          </View>
          <View style={styles.certificationInfo}>
            <Text style={styles.certificationTitle} numberOfLines={2}>
              {certification.title}
            </Text>
            <Text style={styles.certificationIssuer}>{certification.issuer}</Text>
            <View style={styles.certificationMeta}>
              <View style={[styles.statusBadge, { backgroundColor: statusColor }]}>
                <Text style={styles.statusText}>{status}</Text>
              </View>
              {certification.category && (
                <View style={styles.categoryBadge}>
                  <Text style={styles.categoryText}>{certification.category}</Text>
                </View>
              )}
            </View>
          </View>
          <View style={styles.certificationActions}>
            <TouchableOpacity
              style={styles.actionButton}
              onPress={() => handleEditCertification(certification)}
            >
              <Ionicons name="create-outline" size={16} color="#0e4e96" />
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.actionButton}
              onPress={() => handleDeleteCertification(certification)}
            >
              <Ionicons name="trash-outline" size={16} color="#EF4444" />
            </TouchableOpacity>
          </View>
        </View>

        <Text style={styles.certificationDescription} numberOfLines={3}>
          {certification.description}
        </Text>

        <View style={styles.certificationFooter}>
          <View style={styles.certificationDetails}>
            <View style={styles.certificationDetailItem}>
              <Ionicons name="calendar-outline" size={14} color="#64748B" />
              <Text style={styles.certificationDetailText}>Issued: {certification.date}</Text>
            </View>
            {certification.expiryDate && certification.expiryDate !== 'No Expiry' && (
              <View style={styles.certificationDetailItem}>
                <Ionicons name="time-outline" size={14} color="#64748B" />
                <Text style={styles.certificationDetailText}>Expires: {certification.expiryDate}</Text>
              </View>
            )}
            {certification.credentialId && (
              <View style={styles.certificationDetailItem}>
                <Ionicons name="card-outline" size={14} color="#64748B" />
                <Text style={styles.certificationDetailText}>ID: {certification.credentialId}</Text>
              </View>
            )}
          </View>

          {certification.verificationUrl && (
            <TouchableOpacity style={styles.verifyButton}>
              <Ionicons name="checkmark-circle-outline" size={16} color="#10B981" />
              <Text style={styles.verifyButtonText}>Verify</Text>
            </TouchableOpacity>
          )}
        </View>
      </TouchableOpacity>
    );
  };

  // Render filters modal
  const renderFiltersModal = () => (
    <Modal
      visible={showFilters}
      transparent={true}
      animationType="slide"
      onRequestClose={() => setShowFilters(false)}
    >
      <View style={styles.filtersModalContainer}>
        <View style={styles.filtersModalContent}>
          <View style={styles.filtersModalHeader}>
            <Text style={styles.filtersModalTitle}>Filters & Sort</Text>
            <TouchableOpacity
              style={styles.filtersModalCloseButton}
              onPress={() => setShowFilters(false)}
            >
              <Ionicons name="close" size={24} color="#0F172A" />
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.filtersModalBody}>
            {/* Category Filter */}
            <View style={styles.filterSection}>
              <Text style={styles.filterSectionTitle}>Category</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <View style={styles.filterOptions}>
                  {categories.map((category) => (
                    <TouchableOpacity
                      key={category}
                      style={[
                        styles.filterOption,
                        selectedCategory === category && styles.filterOptionSelected
                      ]}
                      onPress={() => setSelectedCategory(category)}
                    >
                      <Text style={[
                        styles.filterOptionText,
                        selectedCategory === category && styles.filterOptionTextSelected
                      ]}>
                        {category}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </ScrollView>
            </View>

            {/* Status Filter */}
            <View style={styles.filterSection}>
              <Text style={styles.filterSectionTitle}>Status</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <View style={styles.filterOptions}>
                  {statuses.map((status) => (
                    <TouchableOpacity
                      key={status}
                      style={[
                        styles.filterOption,
                        selectedStatus === status && styles.filterOptionSelected
                      ]}
                      onPress={() => setSelectedStatus(status)}
                    >
                      <Text style={[
                        styles.filterOptionText,
                        selectedStatus === status && styles.filterOptionTextSelected
                      ]}>
                        {status}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </ScrollView>
            </View>

            {/* Sort Options */}
            <View style={styles.filterSection}>
              <Text style={styles.filterSectionTitle}>Sort By</Text>
              <View style={styles.sortOptions}>
                {[
                  { key: 'date', label: 'Issue Date' },
                  { key: 'title', label: 'Title' },
                  { key: 'issuer', label: 'Issuer' },
                  { key: 'category', label: 'Category' }
                ].map((option) => (
                  <TouchableOpacity
                    key={option.key}
                    style={[
                      styles.sortOption,
                      sortBy === option.key && styles.sortOptionSelected
                    ]}
                    onPress={() => setSortBy(option.key)}
                  >
                    <Text style={[
                      styles.sortOptionText,
                      sortBy === option.key && styles.sortOptionTextSelected
                    ]}>
                      {option.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          </ScrollView>

          <View style={styles.filtersModalFooter}>
            <TouchableOpacity
              style={styles.clearFiltersButton}
              onPress={() => {
                setSelectedCategory('All');
                setSelectedStatus('All');
                setSortBy('date');
              }}
            >
              <Text style={styles.clearFiltersButtonText}>Clear All</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.applyFiltersButton}
              onPress={() => setShowFilters(false)}
            >
              <Text style={styles.applyFiltersButtonText}>Apply</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  // Render edit modal
  const renderEditModal = () => (
    <Modal
      visible={showEditModal}
      transparent={true}
      animationType="slide"
      onRequestClose={() => setShowEditModal(false)}
    >
      <KeyboardAvoidingView
        style={styles.editModalContainer}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <View style={styles.editModalContent}>
          <View style={styles.editModalHeader}>
            <TouchableOpacity
              style={styles.editModalCloseButton}
              onPress={() => setShowEditModal(false)}
            >
              <Ionicons name="close" size={24} color="#0F172A" />
            </TouchableOpacity>
            <Text style={styles.editModalTitle}>
              {editMode === 'add' ? 'Add Certification' : 'Edit Certification'}
            </Text>
            <TouchableOpacity
              style={styles.editModalSaveButton}
              onPress={handleSaveCertification}
            >
              <Text style={styles.editModalSaveButtonText}>Save</Text>
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.editModalBody} showsVerticalScrollIndicator={false}>
            <View style={styles.formContainer}>
              {/* Title */}
              <View style={styles.formField}>
                <Text style={styles.formLabel}>Certification Title *</Text>
                <TextInput
                  style={[styles.formInput, formErrors.title && styles.formInputError]}
                  value={formData.title || ''}
                  onChangeText={(text) => setFormData({ ...formData, title: text })}
                  placeholder="Enter certification title"
                  placeholderTextColor="#94A3B8"
                />
                {formErrors.title && <Text style={styles.formErrorText}>{formErrors.title}</Text>}
              </View>

              {/* Issuer */}
              <View style={styles.formField}>
                <Text style={styles.formLabel}>Issuer *</Text>
                <TextInput
                  style={[styles.formInput, formErrors.issuer && styles.formInputError]}
                  value={formData.issuer || ''}
                  onChangeText={(text) => setFormData({ ...formData, issuer: text })}
                  placeholder="Issuing organization"
                  placeholderTextColor="#94A3B8"
                />
                {formErrors.issuer && <Text style={styles.formErrorText}>{formErrors.issuer}</Text>}
              </View>

              {/* Description */}
              <View style={styles.formField}>
                <Text style={styles.formLabel}>Description *</Text>
                <TextInput
                  style={[styles.formTextArea, formErrors.description && styles.formInputError]}
                  value={formData.description || ''}
                  onChangeText={(text) => setFormData({ ...formData, description: text })}
                  placeholder="Describe the certification and its significance"
                  placeholderTextColor="#94A3B8"
                  multiline
                  numberOfLines={4}
                />
                {formErrors.description && <Text style={styles.formErrorText}>{formErrors.description}</Text>}
              </View>

              {/* Issue Date */}
              <View style={styles.formField}>
                <Text style={styles.formLabel}>Issue Date *</Text>
                <TextInput
                  style={[styles.formInput, formErrors.date && styles.formInputError]}
                  value={formData.date || ''}
                  onChangeText={(text) => setFormData({ ...formData, date: text })}
                  placeholder="e.g., June 2022"
                  placeholderTextColor="#94A3B8"
                />
                {formErrors.date && <Text style={styles.formErrorText}>{formErrors.date}</Text>}
              </View>

              {/* Expiry Date */}
              <View style={styles.formField}>
                <Text style={styles.formLabel}>Expiry Date</Text>
                <TextInput
                  style={styles.formInput}
                  value={formData.expiryDate || ''}
                  onChangeText={(text) => setFormData({ ...formData, expiryDate: text })}
                  placeholder="e.g., June 2025 or 'No Expiry'"
                  placeholderTextColor="#94A3B8"
                />
              </View>

              {/* Category */}
              <View style={styles.formField}>
                <Text style={styles.formLabel}>Category</Text>
                <View style={styles.formPickerContainer}>
                  {categories.filter(cat => cat !== 'All').map((category) => (
                    <TouchableOpacity
                      key={category}
                      style={[
                        styles.formPickerOption,
                        formData.category === category && styles.formPickerOptionSelected
                      ]}
                      onPress={() => setFormData({ ...formData, category })}
                    >
                      <Text style={[
                        styles.formPickerOptionText,
                        formData.category === category && styles.formPickerOptionTextSelected
                      ]}>
                        {category}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>

              {/* Credential ID */}
              <View style={styles.formField}>
                <Text style={styles.formLabel}>Credential ID</Text>
                <TextInput
                  style={styles.formInput}
                  value={formData.credentialId || ''}
                  onChangeText={(text) => setFormData({ ...formData, credentialId: text })}
                  placeholder="Certification ID or number"
                  placeholderTextColor="#94A3B8"
                />
              </View>

              {/* Verification URL */}
              <View style={styles.formField}>
                <Text style={styles.formLabel}>Verification URL</Text>
                <TextInput
                  style={styles.formInput}
                  value={formData.verificationUrl || ''}
                  onChangeText={(text) => setFormData({ ...formData, verificationUrl: text })}
                  placeholder="URL to verify this certification"
                  placeholderTextColor="#94A3B8"
                  autoCapitalize="none"
                />
              </View>
            </View>
          </ScrollView>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );

  // Render loading screen
  if (isLoading) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
        
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Ionicons name="arrow-back" size={24} color="#ffffff" />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>All Certifications</Text>
          <View style={{ width: 32 }} />
        </View>

        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#0e4e96" />
          <Text style={styles.loadingText}>Loading certifications...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />

      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="arrow-back" size={24} color="#ffffff" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>All Certifications</Text>
        <TouchableOpacity
          style={styles.addButton}
          onPress={handleAddCertification}
        >
          <Ionicons name="add" size={24} color="#ffffff" />
        </TouchableOpacity>
      </View>

      <Animated.View style={[styles.content, { opacity: fadeAnim }]}>
        {/* Search and Filters */}
        <View style={styles.searchContainer}>
          <View style={styles.searchBar}>
            <Ionicons name="search" size={20} color="#64748B" />
            <TextInput
              style={styles.searchInput}
              placeholder="Search certifications..."
              value={searchQuery}
              onChangeText={setSearchQuery}
              placeholderTextColor="#94A3B8"
            />
            {searchQuery.length > 0 && (
              <TouchableOpacity onPress={() => setSearchQuery('')}>
                <Ionicons name="close-circle" size={20} color="#64748B" />
              </TouchableOpacity>
            )}
          </View>
          <TouchableOpacity
            style={styles.filterButton}
            onPress={() => setShowFilters(true)}
          >
            <Ionicons name="filter" size={20} color="#0e4e96" />
          </TouchableOpacity>
        </View>

        {/* Active Filters */}
        {(selectedCategory !== 'All' || selectedStatus !== 'All') && (
          <View style={styles.activeFiltersContainer}>
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              {selectedCategory !== 'All' && (
                <View style={styles.activeFilter}>
                  <Text style={styles.activeFilterText}>{selectedCategory}</Text>
                  <TouchableOpacity onPress={() => setSelectedCategory('All')}>
                    <Ionicons name="close" size={16} color="#0e4e96" />
                  </TouchableOpacity>
                </View>
              )}
              {selectedStatus !== 'All' && (
                <View style={styles.activeFilter}>
                  <Text style={styles.activeFilterText}>{selectedStatus}</Text>
                  <TouchableOpacity onPress={() => setSelectedStatus('All')}>
                    <Ionicons name="close" size={16} color="#0e4e96" />
                  </TouchableOpacity>
                </View>
              )}
            </ScrollView>
          </View>
        )}

        {/* Certifications List */}
        <FlatList
          data={filteredCertifications}
          renderItem={renderCertificationCard}
          keyExtractor={item => item.id}
          contentContainerStyle={styles.certificationsList}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
          }
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <MaterialCommunityIcons name="certificate-outline" size={60} color="#CBD5E1" />
              <Text style={styles.emptyText}>No certifications found</Text>
              <Text style={styles.emptySubtext}>
                {searchQuery || selectedCategory !== 'All' || selectedStatus !== 'All'
                  ? 'Try adjusting your search or filters'
                  : 'Add your first certification to get started'}
              </Text>
              <TouchableOpacity 
                style={styles.addFirstCertificationButton}
                onPress={handleAddCertification}
              >
                <Text style={styles.addFirstCertificationButtonText}>Add Certification</Text>
              </TouchableOpacity>
            </View>
          }
        />
      </Animated.View>

      {/* Modals */}
      {renderFiltersModal()}
      {renderEditModal()}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#0e4e96',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: '#0e4e96',
  },
  backButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  addButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#ffffff',
    textAlign: 'center',
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F8FAFC',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#64748B',
    fontWeight: '500',
  },
  content: {
    flex: 1,
    backgroundColor: '#F8FAFC',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: '#ffffff',
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  searchBar: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F1F5F9',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginRight: 12,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: '#0F172A',
    marginLeft: 12,
  },
  filterButton: {
    width: 44,
    height: 44,
    borderRadius: 12,
    backgroundColor: '#F1F5F9',
    alignItems: 'center',
    justifyContent: 'center',
  },
  activeFiltersContainer: {
    backgroundColor: '#ffffff',
    paddingHorizontal: 20,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  activeFilter: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#0e4e96' + '20',
    borderRadius: 16,
    paddingHorizontal: 12,
    paddingVertical: 6,
    marginRight: 8,
  },
  activeFilterText: {
    fontSize: 12,
    color: '#0e4e96',
    fontWeight: '500',
    marginRight: 6,
  },
  certificationsList: {
    paddingHorizontal: 20,
    paddingTop: 20,
  },
  certificationCard: {
    backgroundColor: '#ffffff',
    borderRadius: 16,
    marginBottom: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  certificationHeader: {
    flexDirection: 'row',
    marginBottom: 16,
  },
  certificationIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#0e4e96' + '20',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 16,
  },
  certificationInfo: {
    flex: 1,
  },
  certificationTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 4,
  },
  certificationIssuer: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 8,
  },
  certificationMeta: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    marginRight: 8,
  },
  statusText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#ffffff',
  },
  categoryBadge: {
    backgroundColor: '#F1F5F9',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  categoryText: {
    fontSize: 10,
    fontWeight: '500',
    color: '#64748B',
  },
  certificationActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  actionButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#F1F5F9',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 8,
  },
  certificationDescription: {
    fontSize: 14,
    color: '#64748B',
    lineHeight: 20,
    marginBottom: 16,
  },
  certificationFooter: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
  },
  certificationDetails: {
    flex: 1,
  },
  certificationDetailItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  certificationDetailText: {
    fontSize: 12,
    color: '#64748B',
    marginLeft: 6,
  },
  verifyButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#10B981' + '20',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  verifyButtonText: {
    fontSize: 10,
    color: '#10B981',
    fontWeight: '600',
    marginLeft: 4,
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
  },
  emptyText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0F172A',
    marginTop: 16,
    marginBottom: 8,
  },
  emptySubtext: {
    fontSize: 14,
    color: '#64748B',
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 24,
  },
  addFirstCertificationButton: {
    backgroundColor: '#0e4e96',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  addFirstCertificationButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  // Filters Modal Styles
  filtersModalContainer: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  filtersModalContent: {
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: height * 0.7,
  },
  filtersModalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  filtersModalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0F172A',
  },
  filtersModalCloseButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#F1F5F9',
    alignItems: 'center',
    justifyContent: 'center',
  },
  filtersModalBody: {
    flex: 1,
    paddingHorizontal: 20,
  },
  filterSection: {
    marginVertical: 16,
  },
  filterSectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 12,
  },
  filterOptions: {
    flexDirection: 'row',
  },
  filterOption: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: '#F1F5F9',
    marginRight: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  filterOptionSelected: {
    backgroundColor: '#0e4e96',
    borderColor: '#0e4e96',
  },
  filterOptionText: {
    fontSize: 14,
    color: '#64748B',
    fontWeight: '500',
  },
  filterOptionTextSelected: {
    color: '#ffffff',
  },
  sortOptions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  sortOption: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: '#F1F5F9',
    marginRight: 8,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  sortOptionSelected: {
    backgroundColor: '#0e4e96',
    borderColor: '#0e4e96',
  },
  sortOptionText: {
    fontSize: 14,
    color: '#64748B',
    fontWeight: '500',
  },
  sortOptionTextSelected: {
    color: '#ffffff',
  },
  filtersModalFooter: {
    flexDirection: 'row',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: '#E2E8F0',
  },
  clearFiltersButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: '#F1F5F9',
    alignItems: 'center',
    marginRight: 8,
  },
  clearFiltersButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#64748B',
  },
  applyFiltersButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: '#0e4e96',
    alignItems: 'center',
    marginLeft: 8,
  },
  applyFiltersButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff',
  },
  // Edit Modal Styles
  editModalContainer: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  editModalContent: {
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: height * 0.9,
    minHeight: height * 0.6,
  },
  editModalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  editModalCloseButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#F1F5F9',
    alignItems: 'center',
    justifyContent: 'center',
  },
  editModalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0F172A',
  },
  editModalSaveButton: {
    backgroundColor: '#0e4e96',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
  },
  editModalSaveButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff',
  },
  editModalBody: {
    flex: 1,
    paddingHorizontal: 20,
  },
  formContainer: {
    paddingVertical: 20,
  },
  formField: {
    marginBottom: 20,
  },
  formLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 8,
  },
  formInput: {
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
    color: '#0F172A',
    backgroundColor: '#ffffff',
  },
  formInputError: {
    borderColor: '#EF4444',
  },
  formTextArea: {
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
    color: '#0F172A',
    backgroundColor: '#ffffff',
    minHeight: 100,
    textAlignVertical: 'top',
  },
  formErrorText: {
    fontSize: 12,
    color: '#EF4444',
    marginTop: 4,
  },
  formPickerContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  formPickerOption: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    backgroundColor: '#ffffff',
  },
  formPickerOptionSelected: {
    backgroundColor: '#0e4e96',
    borderColor: '#0e4e96',
  },
  formPickerOptionText: {
    fontSize: 14,
    color: '#64748B',
    fontWeight: '500',
  },
  formPickerOptionTextSelected: {
    color: '#ffffff',
  },
});
